import { BuildLiveChatUrlParams } from './catalogLiveShop.types';

export const LIVE_SHOP_CHAT_MIN_HEIGHT = 200;
export const LIVE_SHOP_CHAT_SECTION_CHROME_HEIGHT = 52;
export const LIVE_SHOP_CHAT_HEADER_HEIGHT = 48;
export const LIVE_SHOP_CHAT_IFRAME_MIN_HEIGHT = 420;

const LIVE_CHAT_DARK_THEME_FLAG = 0;

const YOUTUBE_HOST = 'youtube.com';
const YOUTUBE_SHORT_HOST = 'youtu.be';
const YOUTUBE_WATCH_PATH = 'watch';
const YOUTUBE_VIDEO_PATHS = ['live', 'shorts', 'embed', 'v'];
const YOUTUBE_VIDEO_ID_PATTERN = /^[\w-]{11}$/;
const YOUTUBE_SUBDOMAIN_PATTERN = /^(www|m)\./;

const isYoutubeVideoId = (value: string | null): boolean =>
  !!value && YOUTUBE_VIDEO_ID_PATTERN.test(value);

const parseUrl = (value: string): URL | null => {
  try {
    return new URL(value);
  } catch {
    return null;
  }
};

const isYoutubeWatchUrl = (url: URL, segments: string[]): boolean =>
  segments.length === 1 && isYoutubeVideoId(url.searchParams.get('v'));

export const isYoutubeVideoUrl = (liveLink: string): boolean => {
  const url = parseUrl(liveLink);

  if (!url || (url.protocol !== 'https:' && url.protocol !== 'http:')) {
    return false;
  }

  const hostname = url.hostname.replace(YOUTUBE_SUBDOMAIN_PATTERN, '');
  const segments = url.pathname.split('/').filter(Boolean);

  if (hostname === YOUTUBE_SHORT_HOST) {
    return segments.length === 1 && isYoutubeVideoId(segments[0]);
  }

  if (hostname !== YOUTUBE_HOST) {
    return false;
  }

  if (segments[0] === YOUTUBE_WATCH_PATH) {
    return isYoutubeWatchUrl(url, segments);
  }

  return (
    segments.length === 2 &&
    YOUTUBE_VIDEO_PATHS.includes(segments[0]) &&
    isYoutubeVideoId(segments[1])
  );
};

export const getYoutubeVideoId = (liveLink: string): string | null => {
  try {
    const url = new URL(liveLink);
    const fromQuery = url.searchParams.get('v');
    if (fromQuery) {
      return fromQuery;
    }
    const segments = url.pathname.split('/').filter(Boolean);
    return segments.at(-1) ?? null;
  } catch {
    return null;
  }
};

export const hasCatalogLiveShopVideo = (liveLink?: string): boolean =>
  !!liveLink && !!getYoutubeVideoId(liveLink);

export const buildYoutubeEmbedUrl = (videoId: string): string =>
  `https://www.youtube.com/embed/${videoId}?autoplay=1&start=0&mute=1`;

export const buildYoutubeLiveChatUrl = ({ videoId, embedDomain }: BuildLiveChatUrlParams): string =>
  `https://www.youtube.com/live_chat?v=${videoId}&embed_domain=${embedDomain}&dark_theme=${LIVE_CHAT_DARK_THEME_FLAG}`;
